import { useNavigate } from 'react-router-dom';
import type { PlannedEmployeeWeek, Location } from '@functieloonhuis/shared';
import { formatDateLabel, isSameDay } from '../utils/dateUtils';
import { PlanningDayCell } from './PlanningDayCell';

interface PlanningWeekGridProps {
  planned: PlannedEmployeeWeek[];
  weekDates: Date[];
  filteredDay?: number | null;
  filteredLocation?: Location | null;
  showDepartment?: boolean;
  stickyHeader?: boolean;
}

function isCellDimmed(
  dayOfWeek: number,
  isWorkday: boolean,
  location: Location | null | undefined,
  isHoliday: boolean,
  filteredDay: number | null | undefined,
  filteredLocation: Location | null | undefined,
): boolean {
  const hasFilters = filteredDay || filteredLocation;
  if (!hasFilters) return false;
  if (filteredDay && dayOfWeek !== filteredDay) return true;
  if (filteredLocation) {
    if (isHoliday || !isWorkday || location !== filteredLocation) return true;
  }
  return false;
}

export function PlanningWeekGrid({
  planned,
  weekDates,
  filteredDay,
  filteredLocation,
  showDepartment,
  stickyHeader,
}: PlanningWeekGridProps) {
  const navigate = useNavigate();
  const today = new Date();
  const hasFilters = !!filteredDay || !!filteredLocation;

  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse" role="grid" aria-label="Team planning">
        <thead className={stickyHeader ? 'sticky top-0 z-10 bg-white' : ''}>
          <tr role="row">
            <th className="text-left text-sm font-medium text-gray-600 py-2 px-3 w-48" role="columnheader">
              Medewerker
            </th>
            {showDepartment && (
              <th className="text-left text-sm font-medium text-gray-600 py-2 px-3 w-36" role="columnheader">
                Team
              </th>
            )}
            {weekDates.map((date, i) => {
              const dayOfWeek = i + 1;
              const isToday = isSameDay(date, today);
              const isHighlighted = filteredDay === dayOfWeek;
              return (
                <th
                  key={dayOfWeek}
                  className={`text-center text-sm font-medium py-2 px-1 w-[100px] ${
                    isToday
                      ? 'text-blue-700 bg-blue-50'
                      : isHighlighted
                        ? 'text-indigo-700 bg-indigo-50'
                        : 'text-gray-600'
                  } ${hasFilters && !isHighlighted && !isToday && filteredDay ? 'opacity-40' : ''}`}
                  role="columnheader"
                >
                  <div>{formatDateLabel(date)}</div>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {planned.map(row => (
            <tr key={row.employee.id} role="row" className="hover:bg-gray-50">
              <td className="py-2 px-3">
                <button
                  onClick={() => navigate(`/medewerkers/${row.employee.id}?tab=werkrooster`)}
                  className="flex items-center gap-2 text-left hover:text-blue-600 transition-colors"
                >
                  <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                    <span className="text-blue-600 font-medium text-sm">
                      {row.employee.name.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <span className="text-sm font-medium text-gray-900 truncate">
                    {row.employee.name}
                  </span>
                </button>
              </td>
              {showDepartment && (
                <td className="py-2 px-3">
                  <span className="text-sm text-gray-500 truncate">{row.employee.departmentName || '—'}</span>
                </td>
              )}
              {row.days.map((day, i) => {
                const dimmed = isCellDimmed(
                  i + 1,
                  day.isWorkday,
                  day.location,
                  day.isHoliday,
                  filteredDay,
                  filteredLocation,
                );
                return (
                  <td key={day.date} className="py-2 px-1">
                    <PlanningDayCell
                      entry={day}
                      employeeName={row.employee.name}
                      dimmed={dimmed}
                    />
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
